import { ArrowDown, ArrowUp, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/cn";
import type { SortDir } from "@/lib/listQuery";

/**
 * Scroll container + bordered frame for a data table.
 *
 * The table sizes to its CONTENT (`w-max`), not to the viewport, and the shell
 * scrolls horizontally — a wide ledger behaves like a spreadsheet rather than
 * squeezing thirty columns into the window. `min-w-full` keeps a narrow table
 * filling the frame so short tables still look deliberate.
 *
 * `isolate` is REQUIRED, not decoration. Pinned cells use z-20 (body) and z-30
 * (frozen header corner) to float over the horizontally scrolling columns.
 * Without a stacking context of its own, those z-indexes compete directly with
 * the page toolbar above the table — and win: on the Products page at 1280px
 * the filter toolbar (z-20) wraps to a second line, the approval filter's
 * dropdown overlaps the table, and the checkbox / # / code columns painted over
 * three of its five options, so a click there ticked a product row instead
 * (16/09/2026). Isolating keeps the table's z-indexes comparing only with each
 * other. Safe for every popover used inside a table: Select, SearchableFilter,
 * Modal and ConfirmDialog all render through a portal on <body>.
 */
export function TableShell({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("isolate overflow-x-auto rounded-md border border-line bg-surface", className)}>
      <table className="w-max min-w-full border-collapse text-sm">{children}</table>
    </div>
  );
}

export function Th({
  className,
  align = "left",
  pin,
  pinRight,
  ...props
}: React.ThHTMLAttributes<HTMLTableCellElement> & {
  align?: "left" | "right" | "center";
  /** Freeze this column against horizontal scroll (spreadsheet-style). */
  pin?: boolean;
  /**
   * Freeze against the RIGHT edge. For the row-action column: a twenty-column
   * ledger puts Edit and Delete off-screen, so every single edit costs a
   * horizontal scroll to the end and back. The old system dodged this by making
   * Actions the FIRST column; pinning it right keeps the actions always
   * reachable without spending the frozen left slot, which the product code
   * needs more.
   */
  pinRight?: boolean;
}) {
  return (
    <th
      className={cn(
        "sticky top-0 z-10 select-none whitespace-nowrap border-b border-line bg-sunken px-3 py-2 text-2xs font-semibold uppercase tracking-[0.05em] text-ink-faint",
        // The corner cell is frozen on BOTH axes, so it must outrank the row
        // header (z-10) that scrolls underneath it.
        pin && "left-0 z-30 border-r border-line",
        pinRight && "right-0 z-30 border-l border-line",
        align === "right" && "text-right",
        align === "center" && "text-center",
        align === "left" && "text-left",
        className,
      )}
      {...props}
    />
  );
}

export function Td({
  className,
  align = "left",
  pin,
  pinRight,
  ...props
}: React.TdHTMLAttributes<HTMLTableCellElement> & {
  align?: "left" | "right" | "center";
  /** Freeze this cell against horizontal scroll — pair with <Th pin>. */
  pin?: boolean;
  /** Freeze against the right edge — pair with <Th pinRight>. */
  pinRight?: boolean;
}) {
  return (
    <td
      className={cn(
        "whitespace-nowrap border-b border-line px-3 text-ink",
        // Needs its OWN opaque background or the scrolling columns show through,
        // and must keep following the row's hover state so the row still reads
        // as one row.
        pin &&
          "sticky left-0 z-20 border-r border-line bg-surface group-hover/row:bg-row-hover",
        pinRight &&
          "sticky right-0 z-20 border-l border-line bg-surface group-hover/row:bg-row-hover",
        align === "right" && "text-right",
        align === "center" && "text-center",
        className,
      )}
      style={{ height: "var(--row-h)" }}
      {...props}
    />
  );
}

export function Tr({
  className,
  interactive,
  ...props
}: React.HTMLAttributes<HTMLTableRowElement> & { interactive?: boolean }) {
  return (
    <tr
      className={cn(
        "transition-colors last:[&>td]:border-b-0",
        interactive && "group/row cursor-pointer hover:bg-row-hover",
        className,
      )}
      {...props}
    />
  );
}

/**
 * A column header that sorts the list.
 *
 * Sorting happens on the SERVER — the page shows one page of a 1400-row
 * catalog, so sorting the visible rows would order the page, not the data, and
 * quietly lie about what "highest price" means.
 *
 * The arrow is never blank: an unsorted column shows a faint both-ways chevron
 * so it reads as sortable before you hover it. `aria-sort` carries the same
 * information to screen readers, which cannot see the icon at all.
 */
/**
 * Nút sắp xếp, KHÔNG kèm ô <th>.
 *
 * Tách ra vì có tiêu đề chứa hai khoá sắp xếp (cột "Sub / Cust" của danh sách
 * sản phẩm hiện hai mã chồng nhau). Lồng <SortTh> vào <SortTh> sẽ đẻ ra <th>
 * bên trong <th> — HTML không hợp lệ và trình duyệt tự sửa theo cách khó đoán.
 */
export function SortButton({
  sortKey,
  activeKey,
  dir,
  onToggle,
  children,
  align = "left",
  className,
}: {
  sortKey: string;
  activeKey: string | null;
  dir: SortDir;
  onToggle: (key: string) => void;
  children: React.ReactNode;
  align?: "left" | "right" | "center";
  className?: string;
}) {
  const active = activeKey === sortKey;
  const Icon = !active ? ChevronsUpDown : dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <button
      type="button"
      onClick={() => onToggle(sortKey)}
      className={cn(
        "flex items-center gap-1 px-3 py-2 text-2xs font-semibold uppercase tracking-[0.05em]",
        "hover:text-ink focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand",
        active ? "text-brand" : "text-ink-faint",
        align === "right" && "justify-end",
        align === "center" && "justify-center",
        className,
      )}
    >
      <span>{children}</span>
      <Icon className={cn("size-3 shrink-0", active ? "opacity-100" : "opacity-40")} />
    </button>
  );
}

export function SortTh({
  sortKey,
  activeKey,
  dir,
  onToggle,
  children,
  align = "left",
  pin,
  className,
}: {
  sortKey: string;
  activeKey: string | null;
  dir: SortDir;
  onToggle: (key: string) => void;
  children: React.ReactNode;
  align?: "left" | "right" | "center";
  pin?: boolean;
  className?: string;
}) {
  const active = activeKey === sortKey;
  return (
    <Th
      align={align}
      pin={pin}
      className={cn("p-0", className)}
      aria-sort={active ? (dir === "asc" ? "ascending" : "descending") : "none"}
    >
      <SortButton
        sortKey={sortKey}
        activeKey={activeKey}
        dir={dir}
        onToggle={onToggle}
        align={align}
        className="w-full"
      >
        {children}
      </SortButton>
    </Th>
  );
}
